import {
  addDays,
  addHours,
  differenceInMinutes,
  endOfMonth,
  endOfWeek,
  format,
  set,
  setDay,
  startOfMonth,
  startOfToday,
  startOfWeek
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import en from 'date-fns/locale/en-US';
import nl from 'date-fns/locale/nl';
import dayjs, { Dayjs } from 'dayjs';

import { StrDigit } from 'types';

import { getStore } from 'utils/reduxStore';

en.options = {
  weekStartsOn: 1
};

nl.options = {
  weekStartsOn: 1
};

const locales = {
  en,
  nl
};

let hoursDiff: number;
const getHoursDiff = () => {
  if (hoursDiff) {
    return hoursDiff;
  }
  const { organisationPreferences } = getStore().getState();
  const { timezone } = organisationPreferences;
  const currentDate = new Date();
  hoursDiff = (+currentDate - +utcToZonedTime(currentDate, timezone)) / 3600000;
  return hoursDiff;
};

export const getLocale = () => navigator.language.split('-')[0] as 'en' | 'nl';
export const getDateFnsLocale = () => locales[getLocale()];
export const formatByLocale = (date: Date, pattern = 'P') =>
  format(date, pattern, { locale: getDateFnsLocale() });

export const formatDate = (date: Dayjs | string | undefined) =>
  (date && dayjs(date).format('DD-MM-YYYY')) || '-';

export const formatDateTime = (date: Dayjs | string | undefined) =>
  (date && dayjs(date).format('DD-MM-YYYY HH:mm')) || '-';

export const toLocalISOString = (date: Date) =>
  dayjs(date).add(getHoursDiff(), 'hour').toISOString();

export const toLocalDate = (
  date: `${number}${number}${number}${number}-${number}${number}-${number}${number}`
) => set(new Date(date), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

export const fromISOString = (date: string) =>
  addHours(new Date(date), -getHoursDiff());

export const formatISOString = (date: string, pattern = 'P') =>
  formatByLocale(fromISOString(date), pattern);

const MAX_DATE = '2099-01-01';
const MIN_DATE = '1901-01-01';
export const getMinDate = () => new Date(MIN_DATE);
export const getMaxDate = () => new Date(MAX_DATE);

const isDSTObserved = (date: Date) => {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  return (
    date.getTimezoneOffset() <
    Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
  );
};

export const getDSTDiff = (date: Date) => {
  const todayDST = isDSTObserved(date);
  const tomorrowDST = isDSTObserved(addDays(date, 1));
  return todayDST !== tomorrowDST ? (todayDST ? -1 : 1) : 0;
};

export enum WeekDays {
  SUNDAY,
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY
}

export type WeekDay = Lowercase<keyof typeof WeekDays>;

export type Quarter = 1 | 2 | 3 | 4;

export const getWeekDates = () => [
  setDay(startOfToday(), 1),
  setDay(startOfToday(), 2),
  setDay(startOfToday(), 3),
  setDay(startOfToday(), 4),
  setDay(startOfToday(), 5),
  setDay(startOfToday(), 6),
  setDay(startOfToday(), 0)
];

export const getWeekInterval = (date?: Date) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: endOfWeek(date || new Date(), { weekStartsOn: 1 })
});

export const getWorkweekInterval = (date?: Date) => ({
  startDate: startOfWeek(date || new Date(), { weekStartsOn: 1 }),
  endDate: dayjs(endOfWeek(date || new Date(), { weekStartsOn: 1 }))
    .subtract(2, 'days')
    .toDate()
});

export const getMonthInterval = (date?: Date) => ({
  startDate: startOfMonth(date || new Date()),
  endDate: endOfMonth(date || new Date())
});

export type TimePeriod = `${StrDigit}${StrDigit}:${StrDigit}${StrDigit}`;

export const getDiffInMinutes = (endTime: string, startTime: string) =>
  differenceInMinutes(fromISOString(endTime), fromISOString(startTime));

export const formatMinutesInHours = (
  durationInMinutes: number,
  withTrailingZero?: boolean
) => {
  const totalHours = durationInMinutes / 60;
  const intHours = Math.trunc(totalHours);
  const hours = Math.abs(intHours);
  const minutes = Math.round(Math.abs((totalHours - intHours) * 60));
  const isEn = getLocale() === 'en';
  const h = isEn ? 'h' : 'u';

  return minutes
    ? `${durationInMinutes < 0 ? '-' : ''}${hours}${h}${String(
        minutes
      ).padStart(2, '0')}${isEn ? 'm' : ''}`
    : withTrailingZero
    ? `${hours}${h}00${isEn ? 'm' : ''}`
    : `${hours}${h}`;
};

export const getHoursMinutes = (date: Date | string) =>
  format(typeof date === 'string' ? fromISOString(date) : date, 'HH:mm');
export const getHoursMinutesSeconds = (date: Date | string) =>
  format(typeof date === 'string' ? fromISOString(date) : date, 'HH:mm:ss');

export const formatHoursMinutes = (durationInSeconds: number) => {
  const negative = durationInSeconds < 0 ? `-` : ``;

  const durationInSecondsAbs = Math.abs(durationInSeconds);
  const hours = Math.floor(durationInSecondsAbs / 3600);
  const minutes = Math.round(durationInSecondsAbs / 60 - hours * 60);
  const result =
    hours != 0
      ? `${hours}u${minutes > 9 ? minutes : `0${minutes}`}`
      : `${minutes}min`;
  return `${negative}${result}`;
};

export const formattedHoursMinutesToSeconds = (value: string) => {
  if (value === null || value === undefined) return NaN;

  if (value.match(/^\d*$/gi)) {
    return +value * 3600; // We consider pure numbers as hours
  } else if (value.match(/^\d{1,2}min$/gi)) {
    const [minutes = 0] = value.split('min');
    if (+minutes > 60) return NaN;
    return +value.split('min')[0] * 60;
  } else if (value.match(/^\d{1,2}u\d{2}/gi)) {
    const [hours = '00', minutes = '00'] = value.split('u');
    if (+hours > 24 || +minutes > 60) return NaN;
    return +hours * 3600 + +minutes * 60;
  }

  return NaN;
};

export const formatHours = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.round(durationInSeconds / 60 - hours * 60);
  return `${hours > 9 ? hours : `0${hours}`}u${
    minutes > 9 ? minutes : `0${minutes}`
  }`;
};

export const getLocalizedMonthNames = (
  width: 'narrow' | 'abbreviated' | 'wide' = 'wide'
): string[] => {
  const locale = getDateFnsLocale();
  return Array(12)
    .fill('')
    .map((v, i) => locale?.localize?.month(i, { width }) || '');
};

export const getLocalizedMonthName = (
  month: number,
  width: 'narrow' | 'abbreviated' | 'wide' = 'wide'
): string => {
  const locale = getDateFnsLocale();
  return locale?.localize?.month(month, { width }) || '';
};

export const getStartDateFromWeekNumber = (week: number, year: number) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const isoWeekStart = simple;

  if (dow <= 4) {
    isoWeekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    isoWeekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }

  return isoWeekStart;
};

export const getHoursAndMinutesFromSeconds = (
  seconds: number
): { h: number; m: number } => {
  if (!seconds) return { h: 0, m: 0 };

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { h: hours, m: minutes };
};

export const formatSecondsToHoursMinutes = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round(seconds / 60 - hours * 60);
  const hoursFormat =
    hours > 0 && hours <= 23
      ? `${hours}u `
      : minutes === 60
      ? `${hours + 1}u `
      : '';

  const minutesFormat = minutes && minutes !== 60 ? `${minutes}m` : '0m';

  return `${hoursFormat}${minutesFormat}`;
};

const DAY_OFFSET = {
  0: '  ',
  1: '⁺¹',
  2: '⁺²',
  3: '⁺³'
};

export const formatTime = (
  input: string | Date | Dayjs | null | undefined,
  baseDate?: Date | Dayjs
) => {
  if (!input) {
    return '-';
  }
  const date = dayjs(typeof input == 'string' ? fromISOString(input) : input);
  return date.format('HH:mm') + formatOffset(date, baseDate);
};

export const formatOffset = (
  input: string | Date | Dayjs | null | undefined,
  baseDate: Date | Dayjs | null | undefined = undefined
) => {
  if (!input || !baseDate) {
    return '';
  }
  const date = dayjs(typeof input == 'string' ? fromISOString(input) : input);
  const timeStart = dayjs(
    baseDate || fromISOString('1970-01-01T00:00:00+01:00')
  );
  const dayOffset = date.diff(timeStart, 'days');
  return DAY_OFFSET[dayOffset] || '⁺⁺';
};

export const converterForDate = (date: Date) =>
  dayjs(date).format('MMM D, YYYY');

export const converterForDateTime = (date: Date) =>
  dayjs(date).format('MMM D, YYYY HH:mm:ss A');

export const converterForDateAndTime = (date: Date) =>
  dayjs(date).format('D-MM-YYYY HH:mm');

export const shouldDisableDateInRange = (
  date: Date | Dayjs,
  validFrom?: Date | Dayjs | null | undefined,
  validTo?: Date | Dayjs | null | undefined
): boolean => {
  return (
    dayjs(validFrom || NaN).isAfter(date, 'day') ||
    dayjs(validTo || NaN).isBefore(date, 'day')
  );
};

export const shouldDisableDateAfterYears =
  (years: number) =>
  (date: Date | Dayjs): boolean => {
    const maxDate = dayjs().endOf('year').add(years, 'year');
    return dayjs(date).isAfter(maxDate, 'day');
  };

export const shouldDisableDateAfterFirstOfMonth = (date: Date | Dayjs) => {
  if (!date) return false;
  const dayjsDate = dayjs(date);
  return dayjsDate.date() !== 1;
};
